import ChunkBase from './ChunkBase';
import { EntityFactory } from './EntityFactory';
import { ChunkStage, ChunkStageDetails, ChunkStageValues, World, WorldRelic } from './worlds/world';
import { PlatformFactory } from './PlatformFactory';
import { TrapFactory } from './TrapFactory';
import { GameDataHandler } from '../GameDataHandler';
import { RelicNameValues } from './relics/relic';
import { RunSettingsHandler } from './RunSettingsHandler';
import { chunkConditionsLookup } from './ChunkConditions';

export const BodyTypes = {
  Ground: 'ground',
  Platform: 'platform',
  Player: 'player',
  BreakableBlocker: 'breakableBlocker',
  TouchTrap: 'touchTrap',
  Relic: 'relic',
  GemMagnet: 'gemMagnet'
} as const;

// export interface SpawnedChunk {
//   startPoint: number;
//   endPoint: number;
//   chunk: ChunkBase;
// }

export class LevelManager {
  protected scene: Phaser.Scene;
  protected currentWorld: World;
  protected backgroundLayer: Phaser.GameObjects.Layer | undefined;
  protected foregroundLayer: Phaser.GameObjects.Layer | undefined;
  /** The x value of the end of the last spawned chunk. */
  protected chunkEndX: number;
  /** The y value of the end of the last spawned chunk. */
  protected chunkEndY: number;
  /** The x value that the player needs to exceeded to finish the level. Normally the same as  chunkEndX till the final chunk has spawned. */
  protected levelEndX: number;
  /** Relics from this world that we need to spawn. */
  protected relicsLeftToSpawn: WorldRelic[];
  protected nextRelic: WorldRelic | undefined;
  protected spawnedChunks: ChunkBase[];
  protected spawnedChunkCountByStage: Map<ChunkStageValues, number>;
  protected spawnedChunkCountByType: Map<typeof ChunkBase, number>;
  protected checkpoints: Phaser.Math.Vector2[];
  /** Have we spawned a start chunk? */
  protected isStart: boolean;
  /** Have we spawned a final chunk? */
  protected isEnd: boolean;
  protected currentChunkIndex: number;
  protected lastChunkSpawned: typeof ChunkBase | undefined;

  protected platformFactory: PlatformFactory;
  protected trapFactory: TrapFactory;
  protected entityFactory: EntityFactory;

  constructor(scene: Phaser.Scene, world: World) {
    this.scene = scene;
    this.currentWorld = world;

    this.chunkEndX = 0;
    this.chunkEndY = 0;
    this.levelEndX = 0;

    this.relicsLeftToSpawn = [];

    this.spawnedChunks = [];
    this.spawnedChunkCountByStage = new Map();
    this.spawnedChunkCountByType = new Map();
    this.checkpoints = [];

    this.isStart = true;
    this.isEnd = false;
    this.currentChunkIndex = 0;

    this.platformFactory = new PlatformFactory(this.scene);
    this.trapFactory = new TrapFactory(this.scene);
    this.entityFactory = new EntityFactory(this.scene);
  }

  get FurthestRightChunkX() {
    return this.chunkEndX;
  }

  get LevelEndX() {
    return this.levelEndX;
  }

  get Checkpoints(): Phaser.Math.Vector2[] {
    return this.checkpoints;
  }

  public init(backgroundLayer: Phaser.GameObjects.Layer, foregroundLayer: Phaser.GameObjects.Layer) {
    this.backgroundLayer = backgroundLayer;
    this.foregroundLayer = foregroundLayer;

    this.platformFactory.init();
    this.trapFactory.init();
    this.entityFactory.init();

    this.scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  public changeWorld(world: World) {
    this.currentWorld = world;
  }

  public setupLevel(chunkStartX: number, chunkStartY: number, initialDistanceToGenerate: number) {
    this.isStart = true;
    this.isEnd = false;

    this.chunkEndX = chunkStartX;
    this.chunkEndY = chunkStartY;
    this.levelEndX = chunkStartX;

    this.currentChunkIndex = 0;

    this.clearLevel();

    this.relicsLeftToSpawn = this.currentWorld.relics.slice();

    // Filter out owned relics.
    if (!RunSettingsHandler.shouldSpawnChunksForCollectedRelics(this.scene)) {
      let collectedRelics = this.scene.game.registry.get(GameDataHandler.keys.CollectedRelics) as RelicNameValues[];
      this.relicsLeftToSpawn = this.relicsLeftToSpawn.filter((worldRelic) => collectedRelics.indexOf(worldRelic.relic) < 0);
    }

    this.relicsLeftToSpawn.sort((a, b) => a.distance - b.distance);
    this.getNextRelic();

    this.spawnChunksToCoverDistance(initialDistanceToGenerate);
  }

  public clearLevel() {
    for (const chunk of this.spawnedChunks) {
      chunk.destroy();
    }

    this.spawnedChunks = [];
    this.spawnedChunkCountByStage.clear();
    this.spawnedChunkCountByType.clear();
    this.checkpoints = [];
  }

  public resetLevel() {
    // Respawn all entities.
    this.spawnedChunks.forEach((chunk) => {
      chunk.reset();
    });
  }

  public shiftLevel(newShiftedX: number): number {
    let oldEnd = this.chunkEndX;
    this.chunkEndX = newShiftedX;

    this.spawnedChunks.forEach((chunk) => {
      chunk.shift(this.chunkEndX, this.chunkEndY);
      this.chunkEndX = chunk.AbsRightSnapPointX;
    });

    // Last chunk in the array should be highest positive X chunk.
    this.levelEndX = this.spawnedChunks[this.spawnedChunks.length - 1].FinishPointX;

    let offset = oldEnd - this.chunkEndX;
    return offset;
  }

  public spawnChunksAhead(rightViewBoundsX: number, safeSpawnDistance: number): boolean {
    let hasMadeChunks = false;

    // Generate new chunks if needed.
    if (this.chunkEndX <= rightViewBoundsX + safeSpawnDistance && !this.isEnd) {
      this.spawnChunksToCoverDistance(rightViewBoundsX + safeSpawnDistance - this.chunkEndX);
      hasMadeChunks = true;
    }

    return hasMadeChunks;
  }

  public despawnChunksBehind(leftViewBoundsX: number, safeDespawnDistance: number) {
    // Despawn chunks past the camera.
    let despawnPoint = leftViewBoundsX - safeDespawnDistance;
    let despawnedChunks = 0;
    for (let i = 0; i < this.spawnedChunks.length; i++) {
      const chunk = this.spawnedChunks[i];

      if (chunk.AbsRightSnapPointX < despawnPoint) {
        this.despawnChunk(chunk);
        despawnedChunks++;
      } else {
        break;
      }
    }

    if (despawnedChunks > 0) {
      console.log(`Splicing ${despawnedChunks} chunks. Current chunks ${this.spawnedChunks.length}`);
      this.spawnedChunks.splice(0, despawnedChunks);
      console.log(`Post splice chunks ${this.spawnedChunks.length}.`);
    }
  }

  // Tear down.
  public endLevel() {
    for (const chunk of this.spawnedChunks) {
      this.despawnChunk(chunk);
    }

    this.spawnedChunks = [];
    this.spawnedChunkCountByStage.clear();
    this.spawnedChunkCountByType.clear();
    this.checkpoints = [];

    // this.clearLevel();
  }

  public spawnChunksToCoverDistance(distance: number) {
    let loopCount = 0;
    let startPoint = this.chunkEndX;
    while (this.chunkEndX - startPoint < distance && loopCount < 100 && !this.isEnd) {
      this.spawnNextChunk();
      loopCount++;
    }
  }

  public spawnNextChunk() {
    let order: ChunkStageDetails = this.currentWorld.stageOrdering[this.currentChunkIndex];
    let chunks = this.currentWorld.chunks[order.name]?.slice() ?? [];

    let refreshRelic = false;

    // Check if we need to spawn a relic
    //  or change to the next stage.
    if (this.nextRelic && this.chunkEndX >= this.nextRelic.distance && this.nextRelic.chunks.length > 0) {
      chunks = this.nextRelic.chunks.slice();
      refreshRelic = true;
    } else if (this.shouldAdvanceStage(order)) {
      // if (order.next) {
      //   let index = this.currentWorld.stageOrdering.findIndex((chunkPlacement, index) => chunkPlacement.name == order.next);
      //   if () {

      //   }
      // } else

      this.advanceToNextStage();
      order = this.currentWorld.stageOrdering[this.currentChunkIndex];
      chunks = this.currentWorld.chunks[order.name]?.slice() ?? [];
      return;
    }

    if (order.conditions && order.conditions.length > 0) {
      let shouldSkipToNextStage = false;

      for (const conditionName of order.conditions) {
        let chunkCondition = chunkConditionsLookup[conditionName];
        ({
          selectedChunks: chunks,
          stageOrdering: order,
          shouldSkipToNextStage: shouldSkipToNextStage
        } = chunkCondition.shouldGenerate(this.scene, chunks, order, shouldSkipToNextStage));
      }

      if (shouldSkipToNextStage) {
        this.advanceToNextStage();
        order = this.currentWorld.stageOrdering[this.currentChunkIndex];
        chunks = this.currentWorld.chunks[order.name]?.slice() ?? [];
        return;
      }
    }

    // if (this.isStart) {
    //   chunks = this.currentWorld.startChunks;
    //   this.isStart = false;
    // } else if (this.nextRelic && this.chunkEndX >= this.nextRelic.distance && this.nextRelic.chunks.length > 0) {
    //   chunks = this.nextRelic.chunks;
    //   refreshRelic = true;
    // } else if (this.chunkEndX >= this.currentWorld.distanceBeforeEnd && this.currentWorld.endChunks.length > 0) {
    //   chunks = this.currentWorld.endChunks;
    //   this.isEnd = true;
    // }

    if (chunks.length == 0 || this.isEnd) return;

    // Make sure we don't see the same chunk back to back.
    if (chunks.length > 1 && this.lastChunkSpawned) {
      let index = chunks.indexOf(this.lastChunkSpawned);
      if (index >= 0) chunks.splice(index, 1);
    }

    let randomChunk = this.pickRandomChunk(chunks);
    this.lastChunkSpawned = randomChunk;
    this.spawnChunk(randomChunk);
    let count = this.spawnedChunkCountByStage.get(order.name) ?? 0;
    this.spawnedChunkCountByStage.set(order.name, count + 1);

    if (refreshRelic) this.getNextRelic();
  }

  protected shouldAdvanceStage(order: ChunkStageDetails): boolean {
    let completedRushes = RunSettingsHandler.getCompletedRushes(this.scene);
    return (
      (order.distance != null && this.chunkEndX >= order.distance) ||
      (order.count != null && this.getChunkCountByStage(order.name) >= order.count) ||
      (order.rushMin != null && order.rushMin > completedRushes) ||
      (order.rushMax != null && order.rushMax < completedRushes)
    );
  }

  protected advanceToNextStage() {
    if (this.currentChunkIndex < this.currentWorld.stageOrdering.length - 1) {
      this.currentChunkIndex++;
    } else {
      this.isEnd = true;
    }
  }

  protected getChunkCountByStage(stage: ChunkStageValues): number {
    return this.spawnedChunkCountByStage.get(stage) ?? 0;
  }

  protected getChunkCountByType(chunkPrefab: typeof ChunkBase): number {
    return this.spawnedChunkCountByType.get(chunkPrefab) ?? 0;
  }

  protected pickRandomChunk(chunkPool: typeof ChunkBase[]): typeof ChunkBase {
    let chunkIndex = Phaser.Math.Between(0, chunkPool.length - 1);
    return chunkPool[chunkIndex];
  }

  protected spawnChunk(chunkPrefab: typeof ChunkBase) {
    if (!this.backgroundLayer || !this.foregroundLayer) {
      console.warn('Failed to spawn chunk as missing layers.');
      return;
    }

    console.log(`Spawned chunk ${chunkPrefab.name}`);
    let chunk = new chunkPrefab(this.scene, this.chunkEndX, this.chunkEndY);
    chunk.init(this.platformFactory, this.trapFactory, this.entityFactory, this.backgroundLayer, this.foregroundLayer, this.nextRelic);

    this.chunkEndX = chunk.AbsRightSnapPointX;
    this.chunkEndY = chunk.AbsRightSnapPointY;
    this.levelEndX = chunk.FinishPointX;
    this.checkpoints.push(...chunk.Checkpoints);

    this.spawnedChunks.push(chunk);
    let count: number = this.getChunkCountByType(chunkPrefab);
    this.spawnedChunkCountByType.set(chunkPrefab, count + 1);
    // this.chunkPoints.set(this.chunkEndX, chunk);
  }

  protected despawnChunk(chunk: ChunkBase) {
    console.log(`Despawned chunk ${chunk.constructor.name}`);
    chunk.recycle(-4000, 300);
    // chunk.destroy();
  }

  protected getNextRelic() {
    if (this.relicsLeftToSpawn.length > 0 && this.relicsLeftToSpawn[0].chunks.length > 0) {
      this.nextRelic = this.relicsLeftToSpawn.shift();
    } else {
      this.nextRelic = undefined;
    }
  }

  protected shutdown() {
    this.clearLevel();
  }
}
